/* PeriodPicker — the app's date-range control.
 *
 * `docs/design/spec/02-components.md` maps the mockups' `.rp*` / `.cal*` / `.presets*` to
 * `<DatePicker.RangePicker presets>`; this is that component, with the design's six preset windows,
 * the calendar glyph on the leading edge, "From / To" placeholders and the panel's footer hint.
 * Every period filter in the redesign uses it — the mockups draw the same control on the photo
 * report, visits, surveys and the merchandiser detail page.
 *
 * Anything visual lives in `period-picker.scss` / `src/theme/antd.js`, never inline: the panel's
 * geometry is the reason this is one component rather than a prop bag repeated per page.
 *
 * ```jsx
 * <PeriodPicker value={range} onChange={setRange} />   // range: [dayjs, dayjs] | null
 * ```
 */
import React, { useState } from "react";
import { DatePicker } from "antd";
import { ArrowRightOutlined, CalendarOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";

import { ENTITY_DROPDOWN_SETTINGS } from "../config/lang";
import dayjs from "../util/dayjs";
import { translate } from "../util/util";
import "./period-picker.scss";

/** The default window a cleared period falls back to: two weeks, today inclusive. */
export const DEFAULT_RANGE_DAYS = 14;

/** `n` days ending today, inclusive — so 14 is today and the 13 days before it, not 15 days. */
const lastDays = n => [
  dayjs()
    .subtract(n - 1, "day")
    .startOf("day"),
  dayjs().startOf("day"),
];

/** The window a page starts on, and the one a cleared field returns to. */
export const defaultRange = () => lastDays(DEFAULT_RANGE_DAYS);

/** A single day: the window the busiest lists start on. */
export const todayRange = () => lastDays(1);

/**
 * Hold a range inside `maxDays`, keeping its END and pulling the start forward.
 *
 * The end is the anchor because these ranges are almost always "up to now" — a shared URL with a
 * year in it means "the recent stuff", and truncating the far end would answer with data from a
 * year ago. Returns the range untouched when it already fits, so an equality check upstream can
 * tell whether anything was clamped.
 *
 * ⚠ The unit is DAYS, and it used to be calendar months. A cap is a guard on how much a list
 * asks the database for, and the pages that need the tightest guard need it in days — "14 days"
 * cannot be said in months. The two pages that were on `3` months pass `90` now: same guard, one
 * unit, and no second prop to keep in step.
 */
export const clampRange = (range, maxDays) => {
  if (!maxDays || !range?.[0] || !range?.[1]) return range;
  const earliest = dayjs(range[1]).subtract(maxDays - 1, "day");
  return dayjs(range[0]).isBefore(earliest) ? [earliest, range[1]] : range;
};

/** Days a preset spans, inclusive — the same arithmetic `lastDays` builds them with. */
const presetDays = ([from, to]) => dayjs(to).diff(dayjs(from), "day") + 1;

/* The design's windows, in its order. Built per open rather than per module load, so a tab left
   open overnight does not offer yesterday's "today". */
const PRESETS = [
  ["date_today", () => [dayjs().startOf("day"), dayjs().startOf("day")]],
  [
    "date_yesterday",
    () => [
      dayjs()
        .subtract(1, "day")
        .startOf("day"),
      dayjs()
        .subtract(1, "day")
        .startOf("day"),
    ],
  ],
  [
    "date_last_7_days",
    () => [
      dayjs()
        .subtract(6, "day")
        .startOf("day"),
      dayjs().startOf("day"),
    ],
  ],
  ["date_last_14_days", () => lastDays(DEFAULT_RANGE_DAYS)],
  [
    "date_last_30_days",
    () => [
      dayjs()
        .subtract(29, "day")
        .startOf("day"),
      dayjs().startOf("day"),
    ],
  ],
  ["date_current_month", () => [dayjs().startOf("month"), dayjs().startOf("day")]],
  [
    "date_last_month",
    () => [
      dayjs()
        .subtract(1, "month")
        .startOf("month"),
      dayjs()
        .subtract(1, "month")
        .endOf("month")
        .startOf("day"),
    ],
  ],
];

export default function PeriodPicker({
  value,
  onChange,
  allowFuture = false,
  /** Widest window the field will accept, in DAYS (inclusive). Omit for no cap. */
  maxDays,
  /** Where `allowClear` lands. Omit to clear to nothing, as antd does by default. */
  clearTo,
  className = "",
  onOpenChange: onOpenChangeProp,
  onCalendarChange: onCalendarChangeProp,
  ...rest
}) {
  const lang = useSelector(state => state.me?.language) || "en";
  const t = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);

  /* Which end the panel is waiting for, for the footer hint. antd reports the end that just
     changed, so the next one to pick is the other. */
  const [picking, setPicking] = useState("start");

  /**
   * `disabledDate` is handed the end already picked as `info.from`, which is what makes the cap
   * enforceable in the calendar rather than after the fact: once one end is down, everything
   * further than `maxDays` from it greys out in BOTH directions, so an over-long range cannot
   * be expressed. Without `from` (nothing picked yet) only the future rule applies.
   */
  const disabledDate = (current, info) => {
    if (!current) return false;
    if (!allowFuture && current > dayjs().endOf("day")) return true;
    const from = info?.from;
    if (!maxDays || !from) return false;
    // Inclusive, so a 14-day cap allows a 13-day gap between the two ends.
    return Math.abs(current.diff(from, "day")) > maxDays - 1;
  };

  return (
    <DatePicker.RangePicker
      /* Spread first: the handlers below wrap a caller's own, so they must not be overwritten. */
      {...rest}
      className={`pp ${className}`}
      /* `popupClassName` is deprecated in antd v6; the panel's classes live under `classNames`. */
      classNames={{ popup: { root: "pp-panel" } }}
      value={value}
      /* The mockup puts the calendar glyph on the leading edge; antd's default is a trailing one. */
      prefix={<CalendarOutlined />}
      suffixIcon={null}
      /* The mockup's `.rp__dash` is `arrow_forward` — a straight arrow, which spec/02's icon table
         maps to `ArrowRightOutlined`. antd defaults to `SwapRightOutlined`: one half of a swap
         pair, so its shaft carries a dropped tail on the left and it reads as a broken arrow
         rather than "from → to". */
      separator={<ArrowRightOutlined />}
      allowClear
      placeholder={[t("date_from"), t("date_to")]}
      /* A preset WIDER than the cap is dropped, not clamped. Clamping it left the label lying —
         "Last 30 days" handing back 14 — and a preset the calendar would refuse is a worse bug
         than a missing one. So the cap decides the list: at 14 days the panel offers Today,
         Yesterday, Last 7 and Last 14, and nothing that cannot be picked by hand either. */
      presets={PRESETS.filter(([, range]) => !maxDays || presetDays(range()) <= maxDays).map(([key, range]) => ({
        label: t(key),
        value: range(),
      }))}
      disabledDate={disabledDate}
      renderExtraFooter={() => (
        <span className="pp-hint">
          {t(picking === "start" ? "date_pick_start" : "date_pick_end")}
          {maxDays ? ` · ${t("date_max_range").replace("{n}", maxDays)}` : ""}
        </span>
      )}
      onOpenChange={open => {
        if (open) setPicking("start");
        onOpenChangeProp?.(open);
      }}
      onCalendarChange={(dates, strings, info) => {
        setPicking(info?.range === "start" ? "end" : "start");
        onCalendarChangeProp?.(dates, strings, info);
      }}
      /* A half-picked range is not a range: report null until both ends exist. Clearing reports
         `clearTo` when the caller named one — the field is never empty on those pages, so the
         page cannot end up querying without a window. */
      onChange={next => onChange(next && next[0] && next[1] ? clampRange(next, maxDays) : clearTo?.() ?? null)}
    />
  );
}
